import {
    Component,
    computed,
    effect,
    inject,
    Input,
    OnDestroy,
    output,
    PLATFORM_ID,
    Signal,
    signal,
} from "@angular/core";
import {
    CommonModule,
    isPlatformServer,
    NgOptimizedImage,
} from "@angular/common";
import {
    HOME_PRICE_MAX,
    HOME_PRICE_MIN,
    HOME_PRICE_STEP,
    Plan,
    TRADITIONAL_PCT,
} from "../../modules/pricing/services/pricing.service";
import { FormatDollarsPipe } from "../../modules/pricing/shared/pipes/format-dollars.pipe";

@Component({
    selector: "hs-savings",
    standalone: true,
    imports: [CommonModule, FormatDollarsPipe, NgOptimizedImage],
    templateUrl: "./savings.component.html",
    styleUrls: ["./savings.component.scss"],
})
export class SavingComponent implements OnDestroy {
    @Input({ required: true }) plans!: Signal<Plan[]>;
    @Input({ required: true }) selectedPlanId!: Signal<string>;
    @Input({ required: true }) homePrice!: Signal<number>;

    planSelected = output<string>();
    priceChanged = output<number>();

    readonly HOME_PRICE_MIN = HOME_PRICE_MIN;
    readonly HOME_PRICE_MAX = HOME_PRICE_MAX;
    readonly HOME_PRICE_STEP = HOME_PRICE_STEP;

    private platformId = inject(PLATFORM_ID);

    displaySavings = signal(0);

    private animFrame: number | null = null;
    private prevSavings = 0;

    selectedPlan = computed(
        () =>
            this.plans().find((p) => p.id === this.selectedPlanId()) ??
            this.plans()[0]
    );

    traditionalCost = computed(() => this.homePrice() * TRADITIONAL_PCT);

    sharkCost = computed(() => this.selectedPlan().calcFn(this.homePrice()));

    savings = computed(() => this.traditionalCost() - this.sharkCost());

    savingsPct = computed(() =>
        Math.round((this.savings() / this.traditionalCost()) * 100)
    );

    sliderPct = computed(
        () =>
            ((this.homePrice() - HOME_PRICE_MIN) /
                (HOME_PRICE_MAX - HOME_PRICE_MIN)) *
            100
    );

    constructor() {
        // Animate savings counter whenever it changes
        effect(() => {
            const target = this.savings();
            this.animateTo(target);
        });
    }

    private animateTo(target: number): void {
        if (this.animFrame !== null) {
            cancelAnimationFrame(this.animFrame);
        }
        const start = this.prevSavings;
        const diff = target - start;
        const duration = 400;
        const startTime = performance.now();

        if (!isPlatformServer(this.platformId)) {
            const step = (now: number) => {
                const elapsed = now - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const eased = 1 - Math.pow(1 - progress, 3);
                this.displaySavings.set(Math.round(start + diff * eased));
                if (progress < 1) {
                    this.animFrame = requestAnimationFrame(step);
                } else {
                    this.prevSavings = target;
                    this.animFrame = null;
                }
            };

            this.animFrame = requestAnimationFrame(step);
        }
    }

    onSliderChange(event: Event): void {
        const value = Number((event.target as HTMLInputElement).value);
        this.priceChanged.emit(value);
    }

    selectPlan(id: string): void {
        this.planSelected.emit(id);
    }

    ngOnDestroy(): void {
        if (this.animFrame !== null) {
            cancelAnimationFrame(this.animFrame);
        }
    }
}
